import { useEffect, useRef, useState } from 'react';

// Thought Release: the moment after someone shares on the Sky of Thoughts.
// The thought rises from the share button with a soft trail (star dust at
// night, a thin contrail by day), lands with a glint, and faint threads link it
// to its nearest thoughts for a moment, with a short message: you are not alone.

export type ReleaseBurst = {
  key: string;
  tx: number; ty: number;               // where the new thought lands, in px
  neighbours: { x: number; y: number }[]; // nearby thoughts, in px
};

export const RELEASE_MS = 4200; // how long the moment lasts before the referral prompt

const ease = (k: number) => 1 - Math.pow(1 - k, 3);

export default function ThoughtRelease({ burst, isNight }: { burst: ReleaseBurst | null; isNight: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [showMsg, setShowMsg] = useState(false);
  // Keep the message out of the way: if the thought lands low, show it up top.
  const msgTop = !!burst && burst.ty > window.innerHeight * 0.5;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !burst) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = canvas.clientWidth, H = canvas.clientHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timers = [
      window.setTimeout(() => setShowMsg(true), reduced ? 0 : 1300),
      window.setTimeout(() => setShowMsg(false), RELEASE_MS - 300),
    ];
    if (reduced) return () => timers.forEach(clearTimeout);

    const warm = isNight ? '255, 214, 140' : '255, 255, 255';
    const thread = isNight ? '247, 217, 140' : '255, 255, 255';
    const sx = W / 2, sy = H - 110;
    const { tx, ty } = burst;
    // Control point for a gentle curve.
    const cx = (sx + tx) / 2 + (tx < sx ? -1 : 1) * Math.min(160, Math.abs(tx - sx) * 0.5 + 60);
    const cy = Math.min(sy, ty) - 60;
    const at = (k: number) => ({
      x: (1 - k) * (1 - k) * sx + 2 * (1 - k) * k * cx + k * k * tx,
      y: (1 - k) * (1 - k) * sy + 2 * (1 - k) * k * cy + k * k * ty,
    });
    type Dust = { x: number; y: number; vx: number; vy: number; life: number; r: number };
    const dust: Dust[] = [];
    const t0 = performance.now();
    let raf = 0;

    const glow = (x: number, y: number, r: number, rgb: string, a: number) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(${rgb}, ${a})`);
      g.addColorStop(1, `rgba(${rgb}, 0)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    };

    const frame = (now: number) => {
      const t = (now - t0) / 1000;
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = isNight ? 'lighter' : 'source-over';

      // 1. The rise, with a trail.
      const FLY = 1.15;
      if (t < FLY) {
        const p = at(ease(t / FLY));
        for (let i = 0; i < 3; i++) {
          dust.push({ x: p.x + (Math.random() - 0.5) * 4, y: p.y + (Math.random() - 0.5) * 4, vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4 + 0.15, life: 1, r: 0.8 + Math.random() * 1.6 });
        }
        glow(p.x, p.y, 22, warm, isNight ? 0.9 : 0.95);
      }
      for (const d of dust) {
        d.x += d.vx; d.y += d.vy; d.life -= 0.012;
        if (d.life <= 0) continue;
        if (isNight) glow(d.x, d.y, d.r * 4, warm, d.life * 0.5);
        else {
          ctx.fillStyle = `rgba(255, 255, 255, ${d.life * 0.75})`;
          ctx.beginPath();
          ctx.arc(d.x, d.y, d.r * 1.6, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // 2. The landing: a soft ripple and a glint.
      if (t > FLY - 0.05 && t < FLY + 1.2) {
        const k = (t - FLY) / 1.2;
        ctx.strokeStyle = `rgba(${warm}, ${(1 - k) * 0.7})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(tx, ty, 10 + ease(k) * 46, 0, Math.PI * 2);
        ctx.stroke();
        glow(tx, ty, 40 * (1 - k * 0.5), warm, (1 - k) * 0.8);
        const L = 26 * Math.sin(Math.PI * Math.min(1, k * 1.4));
        ctx.strokeStyle = `rgba(${warm}, ${(1 - k) * 0.9})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(tx - L, ty); ctx.lineTo(tx + L, ty);
        ctx.moveTo(tx, ty - L); ctx.lineTo(tx, ty + L);
        ctx.stroke();
      }

      // 3. You are not alone: threads to the nearest thoughts.
      const T0 = FLY + 0.5, T1 = RELEASE_MS / 1000 - 0.3;
      if (t > T0 && t < T1) {
        const k = (t - T0) / (T1 - T0);
        const a = Math.sin(Math.PI * k);
        const grow = Math.min(1, k * 2.5);
        ctx.lineWidth = 1;
        for (const n of burst.neighbours) {
          ctx.strokeStyle = `rgba(${thread}, ${a * (isNight ? 0.45 : 0.8)})`;
          ctx.setLineDash([2, 5]);
          ctx.beginPath();
          ctx.moveTo(tx, ty);
          ctx.lineTo(tx + (n.x - tx) * grow, ty + (n.y - ty) * grow);
          ctx.stroke();
          ctx.setLineDash([]);
          if (grow >= 1) glow(n.x, n.y, 26, warm, a * 0.55);
        }
      }
      ctx.globalCompositeOperation = 'source-over';
      if (t < RELEASE_MS / 1000) raf = requestAnimationFrame(frame);
      else ctx.clearRect(0, 0, W, H);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); timers.forEach(clearTimeout); };
  }, [burst, isNight]);

  return (
    <>
      <canvas ref={ref} aria-hidden="true" className="absolute inset-0 w-full h-full pointer-events-none z-40" />
      <div
        aria-live="polite"
        className={`absolute left-1/2 -translate-x-1/2 ${msgTop ? 'top-40 md:top-44' : 'bottom-44 md:bottom-48'} z-40 pointer-events-none w-[min(92vw,420px)] text-center px-6 py-4 rounded-3xl backdrop-blur-md border transition-all duration-700 ${
          showMsg ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        } ${isNight ? 'bg-black/35 border-white/10 text-white' : 'bg-white/55 border-white/60 text-[#10151c]'}`}
      >
        <p className="serif italic text-2xl leading-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Your thought is in the sky now.</p>
        <p className={`text-sm mt-1 ${isNight ? 'text-white/70' : 'text-black/60'}`}>Somewhere, someone has felt this too.</p>
      </div>
    </>
  );
}
